import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ServiceCatalogContext = createContext();

export const ServiceCatalogProvider = ({ children }) => {
  const [serviceCatalog, setServiceCatalog] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchServiceCatalog = useCallback(async (forceRefresh = false) => {
    // Check if we have cached data and it's still fresh
    if (!forceRefresh && serviceCatalog && lastFetched) {
      const now = new Date().getTime();
      const timeSinceLastFetch = now - lastFetched;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return; // Use cached data
      }
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/data/full_service_catalog_all_services.json');
      
      if (!response.ok) {
        throw new Error(`Failed to load service catalog: ${response.status}`);
      }

      const data = await response.json();
      
      setServiceCatalog(data);
      setServices(data.services || []);
      setLastFetched(new Date().getTime());
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Service catalog loaded:', data.services?.length || 0, 'services');
      }
    } catch (err) {
      console.error('Error fetching service catalog:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [serviceCatalog, lastFetched]);

  // Initial fetch on mount
  useEffect(() => {
    fetchServiceCatalog();
  }, [fetchServiceCatalog]);

  // Function to find a service by ID
  const findServiceById = useCallback((serviceId) => {
    if (!services) return null;
    return services.find(service => service.id === serviceId);
  }, [services]);

  // Function to filter services by category
  const getServicesByCategory = useCallback((category) => {
    if (!services) return [];
    return services.filter(service => service.category === category);
  }, [services]);

  // Function to search services
  const searchServices = useCallback((query) => {
    if (!services || !query) return services || [];
    const lowercaseQuery = query.toLowerCase();
    return services.filter(service => 
      service.name?.toLowerCase().includes(lowercaseQuery) ||
      service.description?.toLowerCase().includes(lowercaseQuery) ||
      service.category?.toLowerCase().includes(lowercaseQuery)
    );
  }, [services]);

  const value = {
    // Data
    serviceCatalog,
    services,
    
    // State
    loading,
    error,
    lastFetched,
    
    // Actions
    refreshServiceCatalog: () => fetchServiceCatalog(true),
    
    // Helper functions
    findServiceById,
    getServicesByCategory,
    searchServices,
  };

  return (
    <ServiceCatalogContext.Provider value={value}>
      {children}
    </ServiceCatalogContext.Provider>
  );
};

export const useServiceCatalog = () => {
  const context = useContext(ServiceCatalogContext);
  if (!context) {
    throw new Error('useServiceCatalog must be used within a ServiceCatalogProvider');
  }
  return context;
};

export default ServiceCatalogContext;